import React, { useState } from 'react';
import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import { colors, inputs, typography } from '../theme/tokens';
import Button from '../components/Button';
import { ArrowLeft, Eye, EyeSlash, Check } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const PageContainer = styled.div`
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid ${colors.neutral.offWhite};
  background-color: ${colors.neutral.white};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const HeaderTitle = styled(TitleLarge)`
  color: ${colors.brand.laurel};
  margin-bottom: 0.5rem;
`;

const FormContainer = styled.div`
  padding: 2rem;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 500px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-family: ${typography.label.medium.fontFamily};
  font-size: ${typography.label.medium.fontSize};
  line-height: ${typography.label.medium.lineHeight};
  color: ${colors.neutral.darkCharcoal};
  font-weight: 500;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  background: ${inputs.default.background};
  border: ${inputs.default.border};
  color: ${inputs.default.color};
  padding: ${inputs.sizes.default.padding};
  font-size: ${inputs.sizes.default.fontSize};
  border-radius: ${inputs.sizes.default.borderRadius};
  width: 100%;
  font-family: ${typography.paragraph.medium.fontFamily};
  padding-right: 3rem;
  
  &::placeholder {
    color: ${inputs.default.placeholder};
  }
  
  &:focus {
    outline: none;
    border-color: ${inputs.default.focusBorder};
  }
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${colors.neutral.lightCharcoal};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.2s;

  &:hover {
    color: ${colors.neutral.darkCharcoal};
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.reds.bright};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: ${colors.brand.laurel};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const PasswordRequirements = styled.div`
  background-color: ${colors.neutral.offWhite};
  border-radius: 4px;
  padding: 1rem;
  margin-top: 0.5rem;
`;

const RequirementTitle = styled.div`
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${colors.neutral.darkCharcoal};
`;

const RequirementList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  color: ${colors.neutral.charcoal};
  font-size: 0.875rem;
`;

const RequirementItem = styled.li<{ met: boolean }>`
  color: ${props => props.met ? colors.brand.laurel : colors.neutral.charcoal};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Password validation
  const passwordRequirements = {
    length: newPassword.length >= 6,
    match: newPassword === confirmPassword && newPassword.length > 0,
    notEmpty: newPassword.length > 0
  };

  const allRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!allRequirementsMet) {
      setError('Please ensure all password requirements are met');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setIsLoading(true);

    try {
      // First, verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      // If current password is correct, update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);
      showToast('Password changed successfully', 'success');
      
      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Redirect back to settings after a short delay
      setTimeout(() => {
        navigate('/settings');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
      showToast(err.message || 'Failed to change password', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <PageContainer>
      <ContentContainer>
        <Header>
          <HeaderContent>
            <HeaderTitle>Change Password</HeaderTitle>
            <SubtitleMedium>Update your account password</SubtitleMedium>
          </HeaderContent>
          <HeaderButtons>
            <Button 
              variant="outline"
              onClick={handleCancel}
            >
              <ArrowLeft size={20} />
              Back to Settings
            </Button>
          </HeaderButtons>
        </Header>

        <FormContainer>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="currentPassword">Current Password</Label>
              <InputContainer>
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  required
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </PasswordToggleButton>
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="newPassword">New Password</Label>
              <InputContainer>
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  required
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </PasswordToggleButton>
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <InputContainer>
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </PasswordToggleButton>
              </InputContainer>
            </FormGroup>

            <PasswordRequirements>
              <RequirementTitle>Password Requirements:</RequirementTitle>
              <RequirementList>
                <RequirementItem met={passwordRequirements.length}>
                  {passwordRequirements.length && <Check size={16} weight="bold" />}
                  At least 6 characters long
                </RequirementItem>
                <RequirementItem met={passwordRequirements.match}>
                  {passwordRequirements.match && <Check size={16} weight="bold" />}
                  Passwords match
                </RequirementItem>
                <RequirementItem met={passwordRequirements.notEmpty}>
                  {passwordRequirements.notEmpty && <Check size={16} weight="bold" />}
                  New password is not empty
                </RequirementItem>
              </RequirementList>
            </PasswordRequirements>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && (
              <SuccessMessage>
                Password changed successfully! Redirecting to settings...
              </SuccessMessage>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <Button 
                variant="outline" 
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isLoading || !allRequirementsMet || success}
              >
                {isLoading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </div>
          </Form>
        </FormContainer>
      </ContentContainer>
    </PageContainer>
  );
};

export default ChangePassword; 